/**
 * Authentication Utilities - Phase 1
 * 
 * Fetch user information from AWS Cognito and expose raw custom attributes.
 */

import { getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';
import { CognitoSessionInfo, CustomAttributes } from './types';

/**
 * Fetch current user information from Cognito
 * Returns raw custom attributes without any processing
 * 
 * Example Cognito Response:
 * {
 *   "UserAttributes": [
 *     {"Name": "email", "Value": "user@example.com"},
 *     {"Name": "email_verified", "Value": "true"},
 *     {"Name": "custom:partner_type", "Value": "supplier"},
 *     {"Name": "custom:role", "Value": "admin, manager, editor"},
 *     {"Name": "custom:permissions", "Value": {...}},
 *     {"Name": "sub", "Value": "54388458-c0c1-70e3-d071-25c802ce5fc8"}
 *   ]
 * }
 */
export async function getUserInfo(): Promise<CognitoSessionInfo | null> {
  try {
    const { username, userId } = await getCurrentUser();
    const attributes = await fetchUserAttributes();

    // Extract custom attributes - raw values
    const customAttributes: CustomAttributes = {
      partner_type: attributes['custom:partner_type'],
      role: attributes['custom:role'],
      permissions: attributes['custom:permissions'],
    };

    // Add any other custom attributes
    Object.keys(attributes).forEach(key => {
      if (key.startsWith('custom:')) {
        const customKey = key.replace('custom:', '');
        customAttributes[customKey] = attributes[key];
      }
    });

    return {
      email: attributes.email || '',
      emailVerified: attributes.email_verified === 'true',
      name: attributes.name,
      sub: userId,
      username,
      customAttributes,
      rawAttributes: attributes, // Store complete attributes for reference
    };
  } catch (error) {
    console.error('Error fetching user info:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}
